import styled from "styled-components/macro";
import {
  ROW_HEIGHT,
  NAV_HEIGHT_PX,
  BABY_BUTTERFLY_MARGIN,
  Z_INDEX,
  BREAKPOINT_TABLET,
  SHINY_ANIMATED,
} from "../../../constants/constants";
import {
  darkGrey,
  darkModeCardBackgroundColorRGBA,
  darkModeModalBackgroundColor,
  medGrey,
} from "../../../constants/colors";
import { CUSTOM_SCROLLBAR_CSS_HORIZONTAL } from "App/CUSTOM_SCROLLBAR_CSS";

const TREND_CHART_WRAPPER_HZ_PADDING = BABY_BUTTERFLY_MARGIN * 2;

export const TITLE_LABEL_HEIGHT = 44;
export const BARSTACK_PADDING_TOP = 28;
export const COUNT_HEIGHT = 44;
export const CENTER_LABEL_HEIGHT = 68;

export const TREND_CHART_TOOLTIP_WIDTH = 120;
export const TOOLTIP_OFFSET_TOP = 8;

export const TREND_CHART_BAR_WIDTH = ROW_HEIGHT * 1.5; // 60
export const TREND_CHART_BAR_PADDING_HZ = 10;
export const TREND_CHART_BAR_PADDING_VERT = 10;

const GRID_PAGE_TREND_CHART_PADDING = 10;

const TRANSPARENT = `rgba(255,255,255,0) `;
const getWhite = (endOpacity: number) => `rgba(255,255,255,${endOpacity}) `;
const darkModeCardBackgroundColorRGB = darkModeCardBackgroundColorRGBA
  .split(",")
  .slice(0, -1)
  .join(",");
const getBlack = (endOpacity: number) =>
  `${darkModeCardBackgroundColorRGB}, ${endOpacity}) `;

function getGradient({
  width,
  endOpacity,
  isDarkMode,
}: {
  width: number;
  endOpacity: number;
  isDarkMode: boolean;
}) {
  const startColor = isDarkMode ? getBlack(endOpacity) : getWhite(endOpacity);
  return `
  background: -moz-linear-gradient(left, ${startColor} 0%, ${TRANSPARENT} ${width}px, ${TRANSPARENT} calc(100% - ${width}px), ${startColor} 100%);
  background: -webkit-gradient(left top, right top, color-stop(0%, ${startColor}), color-stop(${width}px,
    ${TRANSPARENT}), color-stop(calc(100% - ${width}px),
    ${TRANSPARENT}), color-stop(100%, ${startColor}));
  background: -webkit-linear-gradient(left, ${startColor} 0%, ${TRANSPARENT} ${width}px,
   ${TRANSPARENT} calc(100% - ${width}px), ${startColor} 100%);
  background: -o-linear-gradient(left, ${startColor} 0%, ${TRANSPARENT} ${width}px,
   ${TRANSPARENT} calc(100% - ${width}px), ${startColor} 100%);
  background: -ms-linear-gradient(left, ${startColor} 0%, ${TRANSPARENT} ${width}px,
   ${TRANSPARENT} calc(100% - ${width}px), ${startColor} 100%);
  background: linear-gradient(to right, ${startColor} 0%, ${TRANSPARENT} ${width}px,
   ${TRANSPARENT} calc(100% - ${width}px), ${startColor} 100%);
  @media print {
    background: none;
  }
   `;
}

const getLinearGradient = (isDarkMode: boolean) => ({
  mobile: getGradient({ width: 35, endOpacity: 0.7, isDarkMode }),
  desktop: getGradient({ width: 64, endOpacity: 0.9, isDarkMode }),
});
const getLinearGradientCss = (isDarkMode: boolean) => {
  const linearGradient = getLinearGradient(isDarkMode);
  return `
${linearGradient.mobile}
@media (min-width: ${BREAKPOINT_TABLET}px) {
  ${linearGradient.desktop}
}
`;
};

export const OverflowGradientStyles = styled.div<{
  $isDarkMode: boolean;
}>`
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: ${Z_INDEX.L2};
  top: 0;
  bottom: 12px; /* scrollbar height */
  left: -1px;
  right: -1px;
  ${({ $isDarkMode }) => getLinearGradientCss($isDarkMode)}
`;
export const TrendChartWrapperStyles = styled.div<{
  $isGridLayoutPage: boolean;
  $isLoading: boolean;
  $width: number;
  $hasLegend: boolean;
}>`
  position: ${props => (props.$isGridLayoutPage ? "unset" : "relative")};
  height: 100%;
  ${props => (props.$isLoading ? "" : "overflow: auto;")}
  @media print {
    overflow: visible;
  }
  width: 100%;
  box-sizing: border-box;
  ${props => (props.$hasLegend ? "display: flex; flex-direction: column;" : "")}

  &.trendChartWrapper.gridPage {
    padding: ${GRID_PAGE_TREND_CHART_PADDING}px;
  }
  &.trendChartWrapper:not(.gridPage) > div > [role="presentation"] {
    /* PnlPopup moves to the left when we expand the hublet page right-side */
    left: calc(
      100% - ${props => props.$width - NAV_HEIGHT_PX / 2}px
    ) !important;
    top: ${NAV_HEIGHT_PX}px !important;
    right: ${NAV_HEIGHT_PX / 2}px !important;
    overflow: hidden;
    [aria-hidden="true"]:not(.MuiSvgIcon-root):not(fieldset) {
      /* modal backdrop */
      left: calc(100% - ${props => props.$width}px) !important;
    }
  }
  &.trendChartWrapper.gridPage > div > [role="presentation"] {
    /* on grid page, we keep the modal contained inside the grid section */
    overflow: hidden;
    [aria-hidden="true"]:not(.MuiSvgIcon-root):not(fieldset) {
      /* modal backdrop */
      position: absolute !important;
    }
    position: absolute !important;
  }
  .legendWrapper {
    margin: auto;
  }
  ${CUSTOM_SCROLLBAR_CSS_HORIZONTAL}
  z-index: ${Z_INDEX.L1};
`;

export const TrendChartStyles = styled.div<{
  $isGridLayoutPage: boolean;
  $shouldShowLeftAxis: boolean;
  $shouldShowRightAxis: boolean;
  $shouldHideProfitCategories: boolean;
  $isDarkMode: boolean;
}>`
  box-sizing: border-box;
  width: fit-content;
  margin: auto;
  .trendChartBarPlaceholder {
    width: ${TREND_CHART_BAR_WIDTH}px;
    margin: ${TREND_CHART_BAR_PADDING_HZ}px ${TREND_CHART_BAR_PADDING_HZ}px;
    border-radius: 4px;
    height: 90%;
    position: relative;
    background: ${props =>
      props.$isDarkMode ? darkModeModalBackgroundColor : "hsl(0, 0%, 97%)"};
    ${SHINY_ANIMATED};
  }
  &.noRollups .barstackWrapper {
    height: 0% !important;
  }
  position: ${props => (props.$isGridLayoutPage ? "unset" : "relative")};
  padding-top: ${props => (props.$isGridLayoutPage ? "10px" : 0)};
  padding-bottom: ${props => (props.$isGridLayoutPage ? "10px" : 0)};
  padding-left: ${props =>
    props.$shouldShowLeftAxis ? 0 : TREND_CHART_WRAPPER_HZ_PADDING}px;
  padding-right: ${props =>
    props.$shouldShowRightAxis ? 0 : TREND_CHART_WRAPPER_HZ_PADDING}px;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  .barstack {
    height: 100%;
    box-sizing: border-box;
    min-height: calc(
      50% - ${CENTER_LABEL_HEIGHT + COUNT_HEIGHT + BARSTACK_PADDING_TOP}px
    );
    width: 100%;
    padding: 0 ${TREND_CHART_BAR_PADDING_HZ}px;
    display: flex;
    flex-direction: column-reverse;
    transform-origin: ${props =>
      props.$shouldHideProfitCategories ? "bottom" : "top"};
    &.profit {
      transform-origin: bottom;
      justify-content: flex-start;
    }
    .barstackWrapper {
      ${props =>
        props.$shouldHideProfitCategories ? "justify-content: flex-end;" : ""}
      height: 100%;
      display: flex;
      flex-direction: column;
      &.profitWrapper {
        justify-content: flex-end;
      }
    }
  }
  &.internetExplorer {
    .metricLabel {
      margin-top: 0.5em;
      margin-bottom: -0.75em;
    }
  }
  .actions {
    height: 56px;
    .btnPopup {
      border: 1px solid ${darkGrey};
      margin-top: -6px;
      svg {
        transform: scale(1.5);
      }
    }
  }
  .btnTrendChartBar {
    text-transform: none;
    padding: ${TREND_CHART_BAR_PADDING_VERT}px 0;
    display: flex;
    flex-direction: column;
    min-width: ${TREND_CHART_BAR_WIDTH + 2 * TREND_CHART_BAR_PADDING_HZ}px;
    width: ${TREND_CHART_BAR_WIDTH + 2 * TREND_CHART_BAR_PADDING_HZ}px;
    height: 100%;
    .MuiButton-text {
      padding: 0;
    }
    .MuiButton-root {
      flex-grow: 1;
    }
  }
  .callout {
    font-weight: bold;
    .month {
      font-size: 0.7em;
    }
    .dayNum {
      transform: scale(1.2);
    }
    .hour {
      transform: scale(1.2);
    }
    .minutes {
      bottom: -8px;
    }
  }
  .dim {
    opacity: 0.5;
  }
`;

const NoDataStyles = styled.div`
  display: flex;
  align-items: center;
  height: 260px;
  color: ${medGrey};
`;
export const NoDataIndicator = () => (
  <NoDataStyles>No data found in this filter state</NoDataStyles>
);
